import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { WarningCircle } from "@phosphor-icons/react";
import {
  parseCoverBullets, assembleCoverBullets,
  MAX_COVER_BULLETS,
} from "@/lib/coverSnapshot";

/**
 * Structured editor for slide 1 (Cover & Snapshot).
 *
 * Iter67 (Feb 2026) — the truncation warning is now driven off the
 * LIVE preview bullets themselves (not a mirror). Preview slides on
 * this page render alongside the form, and each snapshot bullet has
 * a `data-testid="cover-preview-bullet-{i}"`. We read
 * `scrollHeight > clientHeight` directly on each live element on
 * every content change + window resize + preview mutation, so the
 * warning is literally "what the founder sees, right now".
 *
 * Iter65's mirror approach false-positive'd due to sub-pixel
 * differences in font hinting/hyphenation between mirror and live.
 * Reading the live element removes that entire class of drift bugs.
 *
 * Iter63 baseline behaviour preserved:
 *   • Local `useState` per field so trailing whitespace + special chars
 *     stick mid-typing
 *   • External content changes reconcile via `lastEmittedRef`
 *   • Canonical `-` bulleted string in `slide.content`
 */

const C = {
  border: "rgba(255,255,255,0.08)",
  ink: "#F5F7FA",
  muted: "rgba(255,255,255,0.55)",
  bg: "#0F1B22",
  bgSoft: "#132029",
  warn: "#F59E0B",
};

/** Measure a single live preview bullet by testid. Returns true if the
 *  2-line clamp is cutting content, false otherwise. Returns false
 *  when the element isn't on the page — we'd rather stay silent than
 *  cry wolf. */
function isLiveBulletClipped(index) {
  const el = document.querySelector(`[data-testid="cover-preview-bullet-${index}"]`);
  if (!el) return false;
  // +1 tolerance for sub-pixel rounding differences.
  return el.scrollHeight > el.clientHeight + 1;
}

export default function CoverSnapshotForm({ content, onChange, primary }) {
  const [bullets, setBullets] = useState(() => parseCoverBullets(content));
  const lastEmittedRef = useRef(content || "");
  const [overflows, setOverflows] = useState([false, false, false, false]);
  // Measurement tick counter forces useLayoutEffect to re-read the DOM
  // when we detect preview mutations even if bullets haven't changed
  // (e.g. layout re-flow after fonts finish loading).
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if ((content || "") === lastEmittedRef.current) return;
    setBullets(parseCoverBullets(content));
    lastEmittedRef.current = content || "";
  }, [content]);

  const emit = useCallback((next) => {
    const assembled = assembleCoverBullets(next);
    lastEmittedRef.current = assembled;
    onChange(assembled);
  }, [onChange]);

  const updateBullet = (i, v) => {
    const next = [...bullets];
    next[i] = v;
    setBullets(next);
    emit(next);
  };

  // Measure overflow directly on the live preview bullets. rAF so we
  // read layout AFTER the preview finishes re-laying out for the same
  // content change (React commits the input first, then the mirrored
  // slide preview re-renders on the same tick — rAF pushes our read
  // one frame past that).
  useLayoutEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    const measure = () => {
      const next = bullets.map((b, i) => {
        if (!b || !b.trim()) return false;
        return isLiveBulletClipped(i);
      });
      setOverflows((prev) =>
        prev.length === next.length && prev.every((v, i) => v === next[i])
          ? prev
          : next
      );
    };
    // Two rAFs — first to yield past the current commit, second to yield
    // past the preview's own layout pass.
    raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(measure); });

    window.addEventListener("resize", measure);

    // Observe preview mutations (e.g. font-swap, layout change) so
    // warnings re-evaluate without needing the founder to type.
    let mo = null;
    const previewRoot = document.querySelector('[data-testid="cover-preview-bullet-0"]')?.parentElement?.parentElement;
    if (previewRoot && typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(() => setTick((t) => t + 1));
      mo.observe(previewRoot, { childList: true, subtree: true, characterData: true });
    }

    // Also: if fonts finish loading later, re-measure once.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("resize", measure);
      if (mo) mo.disconnect();
    };
  }, [bullets, tick]);

  return (
    <div data-testid="cover-snapshot-form" className="space-y-4"
      style={{ fontFamily: "'Raleway','Manrope',system-ui,sans-serif" }}>
      <div className="p-3 rounded-md text-[13px] leading-relaxed"
        style={{ backgroundColor: `${primary}14`, border: `1px solid ${primary}33`, color: C.ink }}>
        <strong className="font-extrabold">Cover &amp; Snapshot</strong> uses a canonical layout. Add up to <span className="font-bold">4 bullets</span> that appear at the bottom of the cover — think one-liners on what you do, who you serve, or headline traction. Bullets are clipped to <span className="font-bold">2 lines</span> in the preview; we&apos;ll flag anything that spills past.
      </div>

      <div className="p-4 rounded-lg" style={{ backgroundColor: C.bgSoft, border: `1px solid ${C.border}` }}>
        <div className="text-[12px] font-extrabold uppercase tracking-[0.20em] mb-3" style={{ color: primary }}>
          Cover bullets ({MAX_COVER_BULLETS})
        </div>
        <div className="space-y-2.5">
          {bullets.map((b, i) => {
            const clipped = overflows[i];
            return (
              <div key={i} className="flex items-start gap-2.5">
                <span className="mt-3 shrink-0 rounded-full"
                  style={{ width: 6, height: 6, backgroundColor: primary }} />
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    data-testid={`cover-bullet-${i}`}
                    value={b}
                    onChange={(e) => updateBullet(i, e.target.value)}
                    placeholder={i === 0
                      ? "e.g. We help African SMEs raise seed capital"
                      : "Another headline bullet…"}
                    className="w-full px-3 py-2.5 rounded text-[14px] focus:outline-none"
                    style={{
                      backgroundColor: C.bg,
                      border: `1px solid ${clipped ? C.warn : C.border}`,
                      color: C.ink,
                      fontFamily: "'Raleway','Manrope',system-ui,sans-serif",
                    }}
                  />
                  {clipped && (
                    <div
                      data-testid={`cover-bullet-${i}-clip-warn`}
                      className="mt-1 flex items-center gap-1 text-[10.5px]"
                      style={{ color: C.warn }}
                    >
                      <WarningCircle size={12} weight="fill" />
                      Runs past 2 lines — the preview will clip the tail. Trim to keep it fully visible.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
